import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Slab, MaterialPrefix } from "@/types/slab";
import { calculatePricePerSqft, calculateSqft } from "@/lib/utils";
import { generateSlabCode } from "@/lib/codeGenerator";
import { PriceType } from "@/types/priceCategory";
import { validateSlabForm, SlabFormErrors } from "@/components/slab/SlabFormValidation";
import { toast } from "sonner";

export function useSlabForm(initialSlab: Slab, onUpdate: (slab: Slab) => void, onClose: () => void) {
  const [formData, setFormData] = useState<Slab>({
    ...initialSlab,
    lastUpdated: new Date()
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<SlabFormErrors>({});

  const { data: materialPrefixes } = useQuery({
    queryKey: ['materialPrefixes'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cat_material_prefixes')
        .select('*')
        .order('material_type');
      
      if (!data) return [];
      return data as MaterialPrefix[];
    },
  });

  const { data: priceCategories } = useQuery({
    queryKey: ['priceCategories'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cat_price_groups')
        .select('*')
        .eq('active', true);
      console.log('Price Categories loaded:', data);
      return (data || []).map(category => ({
        ...category,
        price_type: category.price_type as PriceType
      }));
    },
  });

  useEffect(() => {
    const calculatedSqft = calculateSqft(
      formData.dimensions?.length || 0,
      formData.dimensions?.height || 0
    );
    
    const calculatedPricePerSqft = calculatePricePerSqft(
      formData.price || 0,
      calculatedSqft
    );

    if (calculatedPricePerSqft !== formData.pricePerSqft) {
      setFormData(prev => ({
        ...prev,
        pricePerSqft: calculatedPricePerSqft,
        sqft: calculatedSqft
      }));
    }
  }, [formData.price, formData.dimensions]);

  useEffect(() => {
    if (!materialPrefixes?.length || !priceCategories?.length) {
      console.log('Waiting for data:', { materialPrefixes, priceCategories });
      return;
    }
    
    if (!formData.type) {
      console.log('No material type selected yet');
      return;
    }
    
    console.log('Generating code with:', {
      type: formData.type,
      price: formData.price,
      pricePerSqft: formData.pricePerSqft,
      materialPrefixes
    });
    
    const newCode = generateSlabCode(
      formData.type,
      Number(formData.price) || 0,
      Number(formData.pricePerSqft) || 0,
      materialPrefixes,
      priceCategories
    );
    
    if (newCode !== formData.code) {
      console.log('Updating code from', formData.code, 'to', newCode);
      setFormData(prev => ({
        ...prev,
        code: newCode
      }));
    }
  }, [formData.type, formData.price, formData.pricePerSqft, materialPrefixes, priceCategories]);

  const handleImageChange = (newImageUrl: string) => {
    setFormData(prev => ({
      ...prev,
      imageUrl: newImageUrl,
      lastUpdated: new Date()
    }));
  };

  const handleDimensionsChange = (dimension: 'length' | 'height', value: number) => {
    const newDimensions = {
      ...formData.dimensions,
      [dimension]: value
    };
    
    const calculatedSqft = calculateSqft(
      newDimensions.length || 0,
      newDimensions.height || 0
    );
    
    const calculatedPricePerSqft = calculatePricePerSqft(
      formData.price || 0,
      calculatedSqft
    );

    setFormData(prev => ({
      ...prev,
      dimensions: newDimensions,
      sqft: calculatedSqft,
      pricePerSqft: calculatedPricePerSqft,
      lastUpdated: new Date()
    }));
  };

  const handleQuantityChange = (value: number) => {
    setFormData(prev => ({
      ...prev,
      quantity: value,
      lastUpdated: new Date()
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const [isValid, validationErrors] = validateSlabForm(formData);
    if (!isValid) {
      setErrors(validationErrors);
      toast.error("Por favor, corrija os erros no formulário");
      return;
    }

    const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
    if (!isAuthenticated) {
      toast.error("É necessário autenticar-se para salvar alterações");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (materialPrefixes && priceCategories) {
        const newCode = generateSlabCode(
          formData.type,
          formData.price,
          formData.pricePerSqft,
          materialPrefixes,
          priceCategories
        );
        formData.code = newCode;
      }

      const { error } = await supabase
        .from('cat_slabs')
        .update({
          code: formData.code,
          name: formData.name,
          type: formData.type,
          location_id: formData.location_id,
          price: formData.price,
          price_per_sqft: formData.pricePerSqft,
          sqft: formData.sqft,
          quantity: formData.quantity,
          dimensions: formData.dimensions,
          image_url: formData.imageUrl,
          price_group_id: formData.price_group_id,
          last_updated: new Date().toISOString(),
        })
        .eq('id', formData.id);

      if (error) {
        console.error("Erro ao atualizar:", error);
        throw error;
      }

      toast.success("Produto atualizado com sucesso!");
      onUpdate(formData);
      onClose();
    } catch (error) {
      console.error("Erro ao atualizar produto:", error);
      toast.error("Falha ao atualizar produto");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    isSubmitting,
    errors,
    materialTypes: materialPrefixes?.map(mp => mp.material_type) || [],
    handleImageChange,
    handleDimensionsChange,
    handleQuantityChange,
    handleSubmit
  };
}
